import { type IdentitySignal } from '@aysar/contracts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ModalData } from '@/shared/store/reducers/modal/modal.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import { useConfirmMatchMutation, useMemberSignalsQuery } from '../../services/trust/trust.api';

/** What the queue row hands the dialog. Ids and two emails — serializable only. */
export interface ConfirmMatchTarget {
  matchId: string;
  subjectUserId: string | null;
  subjectEmail: string | null;
  matchedEmail: string | null;
  matchedReason: string | null;
  signalType: string;
}

export interface UseConfirmMatchModalLogic {
  target: ConfirmMatchTarget | null;
  /** True when the match refused a registration before any account existed. */
  hasNothingToBan: boolean;
  note: string;
  setNote: (value: string) => void;
  signals: readonly IdentitySignal[];
  isLoadingSignals: boolean;
  didSignalsFail: boolean;
  isBanning: boolean;
  /** The api's refusal, already translated to this module's own words. */
  error: string | null;
  submit: () => Promise<void>;
}

/**
 * The behaviour behind the portal's most destructive dialog.
 *
 * ## `hasNothingToBan` is computed, never guessed
 *
 * A `block` refused the registration *before* the account was written, so the
 * row has no subject to act on and the api answers `no_subject_to_ban` (422).
 * The dialog does not offer a button that is certain to be refused: it says so,
 * and points at `dismiss` — which still records the reviewer's judgement. That
 * is the same fact the api enforces, rendered rather than duplicated: the check
 * here is on `subjectUserId === null`, which is exactly what the service tests.
 *
 * ## The captured signals are context, not decoration
 *
 * A reviewer about to end an account should see what else the platform holds
 * for it — one collision on a shared phone reads differently from three
 * collisions on a phone, an address and a document. A failed load is **not**
 * fatal here: the ban decision is about the match, and refusing to let the
 * dialog open because a supporting panel 500'd would make the queue unworkable
 * for the wrong reason. It says the panel failed and stays usable.
 */
export function useConfirmMatchModalLogic(
  modalData: ModalData | undefined,
  onClose: () => void,
): UseConfirmMatchModalLogic {
  const { t } = useTranslation('trust');
  const toast = useToast();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const target = readTarget(modalData);
  const subjectUserId = target?.subjectUserId ?? null;
  const hasNothingToBan = target !== null && subjectUserId === null;

  const signalsQuery = useMemberSignalsQuery(subjectUserId ?? '', {
    skip: subjectUserId === null,
  });

  const [confirmMatch, { isLoading: isBanning }] = useConfirmMatchMutation();

  const submit = useCallback(async () => {
    if (target === null || subjectUserId === null) {
      return;
    }

    setError(null);

    try {
      await confirmMatch({
        id: target.matchId,
        subjectUserId,
        note: note.trim() === '' ? null : note.trim(),
      }).unwrap();

      toast.success(t('confirm.success'));
      onClose();
    } catch (caught) {
      // Named refusals, in this module's own words. `concurrent_match_review`
      // is the ordinary one and is not a fault: a colleague ruled first, the
      // queue has already refetched, and the operator needs to be told that
      // rather than shown a generic failure they will retry.
      const apiError = toApiError(caught);

      setError(
        apiError.code === 'concurrent_match_review'
          ? t('error.concurrent')
          : apiError.code === 'no_subject_to_ban'
            ? t('error.noSubject')
            : t('error.generic'),
      );
    }
  }, [target, subjectUserId, note, confirmMatch, toast, t, onClose]);

  return {
    target,
    hasNothingToBan,
    note,
    setNote,
    signals: signalsQuery.data?.items ?? [],
    isLoadingSignals: signalsQuery.isLoading,
    didSignalsFail: signalsQuery.error !== undefined,
    isBanning,
    error,
    submit,
  };
}

/**
 * Narrows the modal payload.
 *
 * `ModalData` is `Record<string, unknown>` by design — it survives a reload and
 * a serialization boundary — so every field is checked rather than cast. A
 * dialog that trusted the payload would ban whatever id happened to be in it.
 */
function readTarget(data: ModalData | undefined): ConfirmMatchTarget | null {
  if (data === undefined) {
    return null;
  }

  const matchId = str(data['matchId']);
  const signalType = str(data['signalType']);

  if (matchId === null || signalType === null) {
    return null;
  }

  return {
    matchId,
    subjectUserId: str(data['subjectUserId']),
    subjectEmail: str(data['subjectEmail']),
    matchedEmail: str(data['matchedEmail']),
    matchedReason: str(data['matchedReason']),
    signalType,
  };
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}
